import { css } from 'aphrodite';
import React, { ReactNode } from 'react';
import { FormFieldType, IFormField, SurveyForm } from '../../../../../../extViewmodels';
import { computeVisibleFields } from '../../../../../../surveys/containers/SurveyResponses/shared/hooks/useConditionalVisibility';
import { Checkbox } from '../../../../../components/Checkbox';
import { ISelectBoxOption, SelectBox } from '../../../../../components/SelectBox';
import { Input } from '../../../../../components-v2/Input';
import { baseStyleSheet } from '../../../../../styles/styles';
import { styleSheet } from '../../styles';

interface PreviewCustomFormFieldsProps {
	customFormFields: IFormField<string>[];
	surveyForm?: SurveyForm;
}

export const PreviewCustomFormFields: React.FC<PreviewCustomFormFieldsProps> = ({ customFormFields, surveyForm }) => {
	// Local selection state so the editor can verify visibility rules in the preview.
	const [previewSelections, setPreviewSelections] = React.useState<Record<string, string>>({});

	const visibleFieldIds = React.useMemo(() => {
		if (!surveyForm?.surveyParts?.length) return undefined;
		return computeVisibleFields(surveyForm, previewSelections);
	}, [surveyForm, previewSelections]);

	// Set of fieldIds represented in surveyForm.surveyParts. Fields not listed
	// fall through as visible — protects against stale or partial surveyForm data.
	const surveyPartFieldIds = React.useMemo(() => {
		if (!surveyForm?.surveyParts?.length) return undefined;
		return new Set(
			surveyForm.surveyParts.map(part => (part as { fieldId?: string }).fieldId).filter((id): id is string => !!id)
		);
	}, [surveyForm]);

	const isFieldVisible = (fieldId: string) => {
		if (!visibleFieldIds || !surveyPartFieldIds) return true;
		if (!surveyPartFieldIds.has(fieldId)) return true;
		return visibleFieldIds.has(fieldId);
	};

	const getFieldOptions = (f: IFormField<string>) => {
		const fieldDropdownOptions = f.options?.map(o => {
			return {
				id: o.id || '',
				title: o.label,
				value: o.label,
			};
		});
		const onSelectionChanged = (selectedOption: ISelectBoxOption<string>) => {
			setPreviewSelections(prev => ({ ...prev, [f.id]: selectedOption.id }));
		};
		const onRenderTriggerTitle = (selectedOption: ISelectBoxOption<string>) => {
			return <span>{selectedOption.title as ReactNode}</span>;
		};
		const currentValue = previewSelections[f.id] ?? f.value;
		return (
			<SelectBox
				options={fieldDropdownOptions}
				onRenderSelectedOptionTitle={onRenderTriggerTitle}
				onSelectionChanged={onSelectionChanged}
				placeholder={<span>Select option</span>}
				triggerClassName={css(styleSheet.selectBoxtriggerButton)}
				selectedOption={fieldDropdownOptions?.find((x: { id: string }) => x.id === currentValue) || undefined}
			/>
		);
	};

	const getMultipleOptions = (f: IFormField<string>) => {
		const currentValue = previewSelections[f.id] ?? f.value ?? '';
		const fieldCheckboxSelectedOptions: string[] = currentValue.split(',').filter(Boolean);
		const fieldCheckboxOptions = f.options?.map(o => {
			const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
				const next = e.target.checked
					? [...fieldCheckboxSelectedOptions, o.id]
					: fieldCheckboxSelectedOptions.filter(id => id !== o.id);
				setPreviewSelections(prev => ({ ...prev, [f.id]: next.join(',') }));
			};
			return (
				<Checkbox
					key={o.id}
					id={o.id}
					className={css(styleSheet.checkbox)}
					checked={fieldCheckboxSelectedOptions.includes(o.id)}
					onChange={onChange}
				>
					<span>{o.label}</span>
				</Checkbox>
			);
		});
		return <>{fieldCheckboxOptions}</>;
	};

	return (
		<div data-testid='basic-form-fields'>
			{customFormFields
				?.filter(f => !f.isHidden)
				.filter(f => isFieldVisible(f.id))
				.map(f => {
					if (!f.label) return null;

					return (
						<div key={f.id} className={css(styleSheet.formControlFullWidth)} data-testid='custom-form-field'>
							<label htmlFor={f.id} className={css(styleSheet.label)}>
								{f.label}
								{!f.isOptional ? (
									<>
										{' '}
										<span className={css(baseStyleSheet.required)} data-testid='required-indicator'>
											*
										</span>
									</>
								) : null}
							</label>
							{f.fieldType === FormFieldType.String ? (
								<Input
									autoComplete='off'
									type='text'
									id={f.id}
									styles={[styleSheet.textInput]}
									disabled
									placeholder='PREVIEW ONLY'
									data-testid='custom-field-input'
								/>
							) : null}
							{f.fieldType === FormFieldType.Option ? getFieldOptions(f) : null}
							{f.fieldType === FormFieldType.MultipleOptions ? getMultipleOptions(f) : null}
							{f.fieldType === FormFieldType.FileUpload ? (
								<Input
									autoComplete='off'
									type='text'
									id={f.id}
									styles={[styleSheet.textInput]}
									disabled
									placeholder='File upload (Preview only)'
									data-testid='custom-field-file-upload'
								/>
							) : null}
						</div>
					);
				})}
		</div>
	);
};
